import type { AxiosResponse } from "axios";

// Campaign Status enum
export enum CampaignStatus {
  Draft = 0,
  Processing = 1,
  Completed = 2,
  Failed = 3,
}

// Filter Criteria interface
export interface FilterCriteria {
  isActive?: boolean;
  hasAbandonedCart?: boolean;
  specificProjectIds?: string[];
  specificPhoneNumbers?: string[];
  excludePhoneNumbers?: string[];
  donationDateFrom?: string | null;
  donationDateTo?: string | null;
  abandonedCartDateFrom?: string | null;
  abandonedCartDateTo?: string | null;
  excludeDonatedFromDate?: string | null;
  excludeDonatedToDate?: string | null;
  minAbandonedCartAmount?: number | null;
  maxAbandonedCartAmount?: number | null;
  minDonationAmount?: number | null;
  maxDonationAmount?: number | null;
}

// WhatsApp Campaign interface (list view)
export interface WhatsappCampaign {
  id: number;
  name: string;
  description: string;
  status: CampaignStatus;
  totalRecipientsCount: number;
  sentCount: number;
  failedCount: number;
  hasFinished: boolean;
  createdOn: string;
  completedAt: string | null;
  pendingCount: number;
}

// Recipient status enum
export enum RecipientStatus {
  Queued = 0,
  Sent = 1,
  Failed = 2,
}

// Campaign Recipient interface
export interface CampaignRecipient {
  campaignId: number;
  campaignName: string;
  userId: string;
  userName: string;
  phoneNumber: string;
  status: RecipientStatus;
  whatsAppMessageId: string | null;
  sentAt: string | null;
  failureReason: string | null;
  processedContent: string | null;
}

// WhatsApp Campaign Detail interface (single campaign view)
export interface WhatsappCampaignDetail {
  id: number;
  name: string;
  description: string;
  status: CampaignStatus;
  filterCriteriaViewModel: FilterCriteria;
  recipients: CampaignRecipient[];
}

// WhatsApp Template interface
export interface WhatsappTemplate {
  body: string;
  customParams: null;
  templateId: string;
  templateName: string;
}

// Form data for creating a campaign
export interface CreateCampaignFormData {
  name: string;
  description?: string;
  template: { value: string; label: string } | null;
  isActive?: boolean;
  hasAbandonedCart?: boolean;
  specificProjectIds?: string[];
  specificPhoneNumbers?: string[];
  excludePhoneNumbers?: string[];
  donationDateFrom?: string | null;
  donationDateTo?: string | null;
  abandonedCartDateFrom?: string | null;
  abandonedCartDateTo?: string | null;
  excludeDonatedFromDate?: string | null;
  excludeDonatedToDate?: string | null;
  minAbandonedCartAmount?: number | string | undefined;
  maxAbandonedCartAmount?: number | string | undefined;
  minDonationAmount?: number | string | undefined;
  maxDonationAmount?: number | string | undefined;
}

// API Response types
export interface PaginatedResponse<T> {
  data: T[];
  currentPage: number;
  endPage: number;
  isSuccess: boolean;
  pageSize: number;
  startPage: number;
  statusCode: number;
  totalItems: number;
  totalPages: number;
}


// Query params
export interface CampaignsQueryParams {
  page?: number;
  perSize?: number;
  search?: CampaignsFilterValues | Record<string, unknown>;
}

export interface TemplatesQueryParams {
  page?: number;
  pageSize?: number | null;
}

// Select option type
export interface SelectOption {
  value: string;
  label: string;
}

// API function return types
export type GetCampaignsResponse = AxiosResponse<PaginatedResponse<WhatsappCampaign>>;
export type GetCampaignResponse = AxiosResponse<WhatsappCampaignDetail>;
export type CreateCampaignResponse = AxiosResponse<WhatsappCampaign>;

// Recipients filter values for search form
export interface RecipientsFilterValues {
  userName: string;
  phoneNumber: string;
  status: RecipientStatus | "";
  sentAtFrom: Date | null;
  sentAtTo: Date | null;
}

// Default filter values
export const defaultFilterValues: RecipientsFilterValues = {
  userName: "",
  phoneNumber: "",
  status: "",
  sentAtFrom: null,
  sentAtTo: null,
};

// Search state for recipients filter
export interface RecipientsSearchState {
  show: boolean;
  values: RecipientsFilterValues;
}

// Campaigns list filter values
export interface CampaignsFilterValues {
  searchTerm: string;
  status: CampaignStatus | null;
  createdFrom: Date | null;
  createdTo: Date | null;
}

// Default filter values for campaigns list
export const defaultCampaignsFilterValues: CampaignsFilterValues = {
  searchTerm: "",
  status: null,
  createdFrom: null,
  createdTo: null,
};

// Status options for campaigns filter
export const campaignStatusOptions = [
  { value: CampaignStatus.Draft, name: "مسودة" },
  { value: CampaignStatus.Processing, name: "قيد المعالجة" },
  { value: CampaignStatus.Completed, name: "مكتمل" },
  { value: CampaignStatus.Failed, name: "فشل" },
];

// Has finished options
export const hasFinishedOptions = [
  { value: "true", name: "مكتمل" },
  { value: "false", name: "غير مكتمل" },
];
